// CMP UI — Banner (Variante B, card flottante) + Modale Preferenze (Variante C).
// Coerente col design Sila: usa i token (bg-card/foreground/primary/border) -> dark-mode ready.
import React, { useEffect, useMemo, useState } from 'react';
import { Cookie, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useCookieConsent } from '@/context/CookieConsentContext';

function PolicyLink({ url, children }) {
  const href = url || '/cookie';
  return (
    <a href={href} className="font-semibold text-primary underline underline-offset-2 hover:opacity-80"
       data-testid="cookie-policy-link">{children}</a>
  );
}

export function CookieConsent() {
  const {
    config, stored, bannerOpen, prefsOpen,
    openPreferences, closePreferences, acceptAll, rejectAll, savePreferences,
  } = useCookieConsent();

  const banner = config?.banner || {};
  const categories = useMemo(() => config?.categories || [], [config]);

  // stato locale toggle nella modale
  const [local, setLocal] = useState({});
  useEffect(() => {
    const init = {};
    categories.forEach((c) => {
      if (c.required) return;
      init[c.key] = !!(stored && stored.categories && stored.categories[c.key]);
    });
    setLocal(init);
  }, [categories, stored, prefsOpen]);

  if (!config) return null;

  const showBanner = bannerOpen && !prefsOpen;

  return (
    <>
      {/* ---------------- Banner Variante B (card flottante) ---------------- */}
      {showBanner && (
        <div
          className="fixed bottom-4 right-4 z-[200] w-[calc(100%-2rem)] max-w-sm rounded-2xl border border-border bg-card p-5 text-card-foreground shadow-2xl duration-200 animate-in fade-in slide-in-from-bottom-4 sm:bottom-6 sm:right-6"
          role="dialog"
          aria-live="polite"
          aria-label="Preferenze cookie"
          data-testid="cookie-banner"
        >
          <div className="mb-2 flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Cookie className="h-5 w-5" />
            </span>
            <p className="font-display text-lg font-bold leading-tight">{banner.title || 'Cookie & Privacy'}</p>
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {banner.message}{' '}
            <PolicyLink url={banner.policy_url}>Scopri di più</PolicyLink>
          </p>
          <div className="mt-4 flex flex-col gap-2">
            <Button className="w-full" onClick={acceptAll} data-testid="cookie-accept-all">
              {banner.accept_label || 'Accetta tutto'}
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={rejectAll} data-testid="cookie-reject-all">
                {banner.reject_label || 'Rifiuta'}
              </Button>
              <Button variant="outline" className="flex-1" onClick={openPreferences} data-testid="cookie-open-prefs">
                {banner.prefs_label || 'Preferenze'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Modale Preferenze Variante C ---------------- */}
      {prefsOpen && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4"
             role="dialog" aria-modal="true" aria-label="Impostazioni cookie" data-testid="cookie-prefs-modal">
          <div className="absolute inset-0 bg-foreground/50 backdrop-blur-sm" onClick={closePreferences} />
          <div className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-2xl duration-200 animate-in fade-in zoom-in-95">
            {/* header */}
            <div className="flex items-start gap-3 px-6 pb-2 pt-6">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Cookie className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <p className="font-display text-xl font-bold leading-tight">Impostazioni cookie</p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  Scegli quali categorie attivare. I cookie tecnici sono sempre attivi perché indispensabili.{' '}
                  <PolicyLink url={banner.policy_url}>Cookie Policy</PolicyLink>
                </p>
              </div>
              <button onClick={closePreferences} aria-label="Chiudi"
                      className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      data-testid="cookie-prefs-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* categorie */}
            <div className="max-h-[45vh] overflow-y-auto px-6 py-2">
              {categories.map((cat) => (
                <div key={cat.key}
                     className="flex items-start justify-between gap-4 border-t border-border py-4 first:border-t-0"
                     data-testid={`cookie-cat-${cat.key}`}>
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {cat.label}
                      {cat.required && <span className="ml-2 text-xs font-medium text-muted-foreground">· sempre attivi</span>}
                    </p>
                    <p className="mt-0.5 max-w-sm text-[13px] leading-relaxed text-muted-foreground">{cat.description}</p>
                  </div>
                  <Switch
                    checked={cat.required ? true : !!local[cat.key]}
                    disabled={cat.required}
                    onCheckedChange={(v) => setLocal((s) => ({ ...s, [cat.key]: v }))}
                    aria-label={cat.label}
                    data-testid={`cookie-switch-${cat.key}`}
                  />
                </div>
              ))}
            </div>

            {/* footer */}
            <div className="flex flex-wrap items-center gap-2 border-t border-border px-6 py-4">
              <Button variant="outline" onClick={rejectAll} data-testid="cookie-prefs-reject">Rifiuta tutto</Button>
              <Button variant="outline" onClick={() => savePreferences(local)} data-testid="cookie-prefs-save">
                {banner.save_label || 'Salva preferenze'}
              </Button>
              <span className="flex-1" />
              <Button onClick={acceptAll} data-testid="cookie-prefs-accept">
                {banner.accept_label || 'Accetta tutto'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CookieConsent;
